import { CreditCard } from 'lucide-react';
import { Card, CardContent } from '@/components/ui';
import { DashboardEmptyStateCard, DashboardEmptyStateIcon } from '@/components/dashboard/DashboardEmptyState';
import { MembershipReceiptLink } from '@/components/membership/MembershipReceiptLink';
import { formatCurrency, type Currency } from '@/utils/currency';
import type { MemberPortalMembership } from '@/services/publicClubMembership';

function formatCentsAsMoney(cents: number, currency: string): string {
  return formatCurrency(cents / 100, currency as Currency);
}

function formatPaymentDate(membership: MemberPortalMembership): string {
  const value = membership.paidAt ?? membership.startDate;
  return value ? new Date(value).toLocaleDateString() : '—';
}

const PAYMENT_METHOD_LABELS: Record<string, string> = {
  stripe: 'Card',
  cash: 'Cash',
  eftpos: 'EFTPOS',
  bank_transfer: 'Bank transfer',
  other: 'Other',
};

const PAYMENT_STATUS_LABELS: Partial<Record<MemberPortalMembership['paymentStatus'], string>> = {
  paid: 'Paid',
  unpaid: 'Unpaid',
  refunded: 'Refunded',
};

function isPaymentHistoryEntry(membership: MemberPortalMembership): boolean {
  return membership.paymentStatus === 'paid' || membership.paymentStatus === 'refunded';
}

interface MembershipPaymentHistoryProps {
  clubSlug: string;
  memberships: MemberPortalMembership[];
}

export function MembershipPaymentHistory({
  clubSlug,
  memberships,
}: MembershipPaymentHistoryProps) {
  const payments = memberships
    .filter(isPaymentHistoryEntry)
    .slice()
    .sort((a, b) => {
      const aTime = new Date(a.paidAt ?? a.startDate).getTime();
      const bTime = new Date(b.paidAt ?? b.startDate).getTime();
      return bTime - aTime;
    });

  return (
    <section aria-labelledby="membership-payment-history-heading">
      <h2 id="membership-payment-history-heading" className="mb-3 text-lg font-semibold text-gray-900 dark:text-white">
        Payment history
      </h2>

      {payments.length === 0 ? (
        <DashboardEmptyStateCard>
          <DashboardEmptyStateIcon icon={CreditCard} />
          <h3 className="mb-2 text-lg font-semibold text-gray-900 dark:text-white">No payments yet</h3>
          <p className="text-muted-foreground">Membership payments will appear here after you join or renew.</p>
        </DashboardEmptyStateCard>
      ) : (
        <div className="space-y-3">
          {payments.map((membership) => (
            <Card key={membership.id} className="overflow-hidden border-border/80 bg-card shadow-sm">
              <CardContent className="flex flex-wrap items-start justify-between gap-3 p-4">
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">
                    {membership.membershipType?.name ?? 'Membership'}
                  </p>
                  <p className="text-sm text-muted-foreground">{formatPaymentDate(membership)}</p>
                  {membership.seasonLabel ? (
                    <p className="text-sm text-muted-foreground">Season {membership.seasonLabel}</p>
                  ) : null}
                  {membership.paymentMethod ? (
                    <p className="mt-1 text-sm text-muted-foreground">
                      {PAYMENT_METHOD_LABELS[membership.paymentMethod] ?? membership.paymentMethod}
                    </p>
                  ) : null}
                </div>
                <div className="text-right">
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">
                    {formatCentsAsMoney(membership.totalCents, membership.currency)}
                  </p>
                  <p className="text-sm capitalize text-muted-foreground">
                    {PAYMENT_STATUS_LABELS[membership.paymentStatus] ?? membership.paymentStatus}
                  </p>
                  <MembershipReceiptLink
                    clubSlug={clubSlug}
                    membershipId={membership.id}
                    hasReceipt={membership.hasReceipt}
                    className="mt-1 justify-end"
                  />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </section>
  );
}
